import React from "react";
import { useT } from "@/i18n/LanguageContext";

/**
 * Generic sortable column header. Renders `<label> ▲▼` and cycles through
 * asc → desc → cleared when clicked. When a DIFFERENT column is active,
 * a click here activates this column at asc.
 *
 * Props:
 *   label     – text to display in the header cell
 *   sortKey   – unique key for THIS column (matched against state.key)
 *   state     – { key, dir } from useColumnSort()
 *   onChange  – setter from useColumnSort()
 *   testid    – optional testid prefix (default: `col-sort-${sortKey}`)
 *   align     – "left" (default) | "right"
 */
export function ColumnSortHeader({ label, sortKey, state, onChange, testid, align = "left" }) {
  const { t } = useT();
  const displayLabel = t(label);
  const active = state?.key === sortKey;
  const dir = active ? state?.dir : null;
  const tid = testid || `col-sort-${sortKey}`;
  const nextDir =
    !active ? "asc" :
    dir === "asc" ? "desc" :
    dir === "desc" ? null :   // third click clears the sort
    "asc";
  const title = active
    ? `${t("Sort")} · ${displayLabel} · ${dir} · ${nextDir || t("Default").toLowerCase()}`
    : `${t("Sort")} · ${displayLabel}`;
  return (
    <button
      type="button"
      onClick={() => onChange(nextDir ? { key: sortKey, dir: nextDir } : { key: null, dir: null })}
      className={`inline-flex items-center gap-1 hover:opacity-80 focus:outline-none select-none uppercase tracking-wider ${align === "right" ? "justify-end" : ""} ${active ? "text-slate-900" : ""}`}
      title={title}
      data-testid={`${tid}-toggle`}
    >
      <span>{displayLabel}</span>
      <span className="inline-flex flex-col items-center leading-none ml-0.5" aria-hidden="true">
        <span
          data-testid={`${tid}-asc`}
          className={active && dir === "asc" ? "opacity-100" : "opacity-30"}
          style={{ fontSize: "8px", lineHeight: "8px" }}
        >▲</span>
        <span
          data-testid={`${tid}-desc`}
          className={active && dir === "desc" ? "opacity-100" : "opacity-30"}
          style={{ fontSize: "8px", lineHeight: "8px" }}
        >▼</span>
      </span>
    </button>
  );
}

/**
 * Persist-per-tab column sort. Returns [state, setState] where state is
 * `{ key: string|null, dir: "asc"|"desc"|null }`. Persists in localStorage
 * under `orders-col-sort:<scope>` so BO's chosen sort survives navigation
 * & reload.
 */
export function useColumnSort(scope) {
  const storageKey = `orders-col-sort:${scope || "default"}`;
  const [state, setState] = React.useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && (parsed.key === null || typeof parsed.key === "string")) {
          return { key: parsed.key || null, dir: parsed.dir || null };
        }
      }
    } catch { /* ignore */ }
    return { key: null, dir: null };
  });
  const set = React.useCallback((next) => {
    const val = next && next.key ? { key: next.key, dir: next.dir || "asc" } : { key: null, dir: null };
    setState(val);
    try { localStorage.setItem(storageKey, JSON.stringify(val)); } catch { /* ignore */ }
  }, [storageKey]);
  return [state, set];
}

/**
 * Sort an array by an arbitrary extractor. Handles:
 *   - Strings (case-insensitive, natural-numeric compare)
 *   - Numbers
 *   - Booleans (true above false)
 *   - Dates / ISO strings
 *   - null/undefined values ALWAYS sink to the bottom regardless of direction
 *
 * `extract(order) -> value` — return the sort key for one row.
 * Returns a NEW array. Returns input untouched when `dir` is falsy or list too short.
 */
export function sortByExtractor(list, dir, extract) {
  if (!dir || !Array.isArray(list) || list.length < 2) return list;
  const asc = dir === "asc";
  const isBlank = (v) => v === null || v === undefined || (typeof v === "string" && !v.trim());
  const asDate = (v) => {
    if (v instanceof Date) return v.getTime();
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
      const t = Date.parse(v);
      return Number.isFinite(t) ? t : null;
    }
    return null;
  };
  const asNum = (v) => {
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    if (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v.trim())) return Number(v);
    return null;
  };
  return [...list].sort((a, b) => {
    const av = extract(a);
    const bv = extract(b);
    const ab = isBlank(av);
    const bb = isBlank(bv);
    if (ab && bb) return 0;
    if (ab) return 1;   // blanks sink
    if (bb) return -1;
    // Try boolean
    if (typeof av === "boolean" || typeof bv === "boolean") {
      const an = av ? 1 : 0;
      const bn = bv ? 1 : 0;
      return asc ? an - bn : bn - an;
    }
    // Try dates
    const ad = asDate(av);
    const bd = asDate(bv);
    if (ad !== null && bd !== null) return asc ? ad - bd : bd - ad;
    // Try numbers
    const an = asNum(av);
    const bn = asNum(bv);
    if (an !== null && bn !== null) return asc ? an - bn : bn - an;
    // Fallback: natural string compare (case-insensitive)
    const as = String(av);
    const bs = String(bv);
    return asc
      ? as.localeCompare(bs, undefined, { numeric: true, sensitivity: "base" })
      : bs.localeCompare(as, undefined, { numeric: true, sensitivity: "base" });
  });
}
